import React, { createContext, useEffect, useState, useContext } from 'react';
import { useRouter } from 'next/router';
import chainConfRequest from '../../services/chainConf.service';
import hotelConfRequest from '../../services/hotelConf.service';
import { colors } from '../../styles/theme';
import { LanguageContext } from './Language.context';
import { isMobileOnly } from 'react-device-detect';
import { handlePaperlessVersionWithAutologin } from '../utils/version/paperless-version';

export const ThemeContext = createContext({});

const sessionStorageKey = 'theme';
const isAliceEnabled = true;

const defaultTheme = {
  mainColor: colors.secondary,
  secondaryColor: colors.secondary,
  titleColor: colors.black,
  bgColor: colors.primary,
  isKiosk: false,
  isAliceEnabled: isAliceEnabled,
};

const kioskDefaultTheme = {
  mainColor: colors.secondary,
  secondaryColor: colors.secondary,
  titleColor: colors.white,
  bgColor: 'linear-gradient(220.92deg, #4AC3E2 0%, #2BAD74 100%)',
  isKiosk: true,
  isAliceEnabled: isAliceEnabled,
};

export function ThemeContextProvider({ children }) {
  const router = useRouter();
  const [state, setState] = useState(defaultTheme);
  const actions = {
    setIsKiosk: isKiosk => {
      if (isKiosk) {
        setState(kioskDefaultTheme);
      } else {
        setState(defaultTheme);
      }
    },
    setPartner: partner => {
      updateState({ partner });
      localStorage.setItem('partner', partner);
    },
    goToMultipropertyLanding: () => {
      router.push('/login/hotelSelector');
    },
    saveIsPaperlessMultiproperty: isMultiproperty => {
      localStorage.setItem('kiosk-paperless-multiproperty', isMultiproperty);
    },
    getIsPaperlessMultiproperty: () => {
      return localStorage.getItem('kiosk-paperless-multiproperty') == 'true' || false;
    },
  };

  const { state: languageContextState } = useContext(LanguageContext);

  useEffect(() => {
    var userAgent = window.navigator.userAgent;
    const isKiosk = userAgent.includes('kiosk') || state.isKiosk;

    const partner = new URLSearchParams(window.location.search).get('partner');
    partner && actions.setPartner(partner);

    if (!router.query.hotelId && !router.query.chainId) return;

    const themeString = sessionStorage.getItem(sessionStorageKey);
    const theme = themeString ? JSON.parse(themeString) : {};

    if (
      theme.hotelId == router.query.hotelId &&
      theme.chainId == router.query.chainId &&
      theme.lang == languageContextState.selectedLanguage
    ) {
      updateState({
        ...theme,
        isIframe: window.location !== window.parent.location,
      });
    } else {
      if (
        (!partner &&
          theme.hotelId == router.query.hotelId &&
          theme.chainId == router.query.chainId &&
          theme.lang !== languageContextState.selectedLanguage) ||
        (!partner && theme.hotelId != router.query.hotelId) ||
        theme.chainId != router.query.chainId
      ) {
        localStorage.removeItem('partner');
      }
      let request = null;
      if (router.query.chainId) {
        request = chainConfRequest(
          router.query.chainId,
          isKiosk,
          actions.getIsPaperlessMultiproperty()
        );
      }
      if (router.query.hotelId) {
        request = hotelConfRequest(
          router.query.hotelId,
          isKiosk,
          languageContextState.selectedLanguage,
          actions.getIsPaperlessMultiproperty()
        );
      }
      if (request) {
        request
          .then(conf => {
            const {
              camera_type: cameraTypeAlice,
              documentCapturerType: documentCapturerTypeAlice,
              showBookingList,
              aliceAutomaticCapture,
              paperlessStartCheckinTimeoutSeconds: startCheckinTimeoutSeconds,
              paperlessFinalizeCheckinTimeoutSeconds: finalizeCheckinTimeoutSeconds,
              ...rest
            } = conf;
            const parsedConf = {
              ...rest,
              bgColor: conf.bgColor
                ? conf.bgColor.indexOf('gradient') >= 0
                  ? conf.bgColor
                  : '#' + conf.bgColor
                : '#f2f7fb',
              mainColor: '#' + conf.mainColor,
              secondaryColor: '#' + conf.secondaryColor,
              titleColor: conf.titleColor
                ? '#' + conf.titleColor
                : isKiosk
                ? colors.white
                : colors.black,
              isIframe: window.location !== window.parent.location,
              isKiosk: isKiosk,
              isAliceEnabled: isAliceEnabled,
              hotelId: router.query.hotelId,
              chainId: router.query.chainId,
              cameraTypeAlice,
              documentCapturerTypeAlice,
              lang: languageContextState.selectedLanguage,
              showBookingList,
              aliceAutomaticCapture,
              startCheckinTimeoutSeconds: isKiosk ? startCheckinTimeoutSeconds : 0,
              finalizeCheckinTimeoutSeconds: isKiosk ? finalizeCheckinTimeoutSeconds : 0,
              extraDocument: conf.extraDocument,
              canUseIframe: conf.canUseIframe,
              paperlessVersion: conf.paperlessVersion,
            };
            updateState(parsedConf);
            sessionStorage.setItem(sessionStorageKey, JSON.stringify(parsedConf));

            if (conf.paperlessVersion) {
              const loginToken = localStorage.getItem('login-token');
              handlePaperlessVersionWithAutologin({
                paperlessVersion: conf.paperlessVersion,
                token: loginToken,
              });
            }
          })
          .catch(() => {
            updateState(
              isKiosk
                ? { ...kioskDefaultTheme, lang: languageContextState.selectedLanguage }
                : { ...defaultTheme, lang: languageContextState.selectedLanguage }
            );
            sessionStorage.setItem(
              sessionStorageKey,
              JSON.stringify(
                isKiosk
                  ? { ...kioskDefaultTheme, lang: languageContextState.selectedLanguage }
                  : { ...defaultTheme, lang: languageContextState.selectedLanguage }
              )
            );
          });
      }
    }
  }, [router.query.hotelId, router.query.chainId, languageContextState.selectedLanguage]);

  function updateState(newState) {
    setState(previousState => ({ ...previousState, ...newState }));
  }

  return <ThemeContext.Provider value={{ state, actions }}>{children}</ThemeContext.Provider>;
}
